<!-->╔══════════════════════════════════════════════════════════════════════════════╦═══════╗<- ->
<- ->║                                Pagination Component                          ║ ADMIN ║<- ->
<- ->╚══════════════════════════════════════════════════════════════════════════════╩═══════╝<--->

<template>
    <div class="paginator">
        <div v-if="!recordCount" class="caption no-search-results"><p>No records found</p></div>

        <div v-else>
            <div class="user-controls">
                <div class="caption record-count">
                    <p>{{ recordCount | format }} {{ recordCount == 1 ? "record" : "records" }} found</p>
                </div>

                <div v-if="pageSizeString != recordCount" :class="['page-nav', { 'on-first-page': page == 0, 'on-last-page': page == pages-1 }]">
                    <!-- First Page -->
                    <span class="page-first" @click="page = 0" title="Move to first page"><strong>&lt;&lt;</strong></span>

                    <!-- Page Back -->
                    <span class="page-backward" @click="page = Math.max(page-1, 0)" title="Move back one page"><strong>&lt;</strong></span>

                    <!-- Current Page -->
                    <span class="plain-text-input current-page">
                        <label for="pageString">Current Page</label>
                        <input type="text" name="pageString" id="pageString" v-model="pageString" title="Jump to page" autocomplete="off" />
                    </span>

                    <!-- Page Forward -->
                    <span class="page-forward" @click="page = Math.min(page+1, pages-1)" title="Move forward one page"><strong>&gt;</strong></span>

                    <!-- Last Page -->
                    <span class="page-last" @click="page = pages-1" title="Move to last page"><strong>&gt;&gt;</strong></span>
                </div>

                <!-- Page Size -->
                <div class="plain-text-select page-size">
                    <label for="pageSize">Items Per Page</label>
                    <select v-model="pageSizeString" id="pageSize">
                        <option v-for="size in pageSizes">{{ size }}</option>
                    </select>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .paginator {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        border: 1px solid;
        padding: 1em;
        margin: 1em;
    }

        .paginator .buttons input {
            width: 3em;
            text-align: center;
            font-size: 1.3em;
            font-weight: bold;
        }

        .paginator .buttons > * {
            padding: 0 0.25em;
            font-size: 1em !important;
        }
</style>

<script>
    import * as format from "@/helpers/format"

    export default {
        name: 'Pagination',
        filters: { ...format },

        props: {
            currentPage: Number,
            currentPageSize: Number,
            recordCount: Number,
            maxPageSize: {
                type: Number,
                default: 100
            }
        },

        data() {
            return {
                page: Math.max(0, this.currentPage || 0),
                pageSize: Math.max(0, this.currentPageSize || 10),

                timeout: null
            }
        },

        computed: {
            pages() {
                return Math.ceil(this.recordCount / this.pageSize)
            },

            pageString: {
                get() { return `${this.page + 1}` },
                set(newPageString) {
                    if (this.timeout) clearTimeout(this.timeout)
                    this.timeout = setTimeout(_ => {
                        let newPage = (newPageString - 1)
                        if (isNaN(newPage) && this.page == 0) this.page = -1  //  force a pageString change if we're already at page zero on a NaN entry
                        this.$nextTick(_ => this.page = Math.max(0, Math.min(this.pages - 1, newPage || 0)))
                    }, 1000)
                }
            },

            pageSizes() {
                //  Limit page size to next-size-up from recordCount, not to exceed maxPageSize
                let sizes = [10, 25, 50, 100, 250, 500, 1000].filter(s => s <= this.maxPageSize)
                return sizes.slice(0, sizes.filter(s => s < this.recordCount).length + 1)
            },

            pageSizeString: {
                get() { return `${this.pageSize}` },
                set(newPageSize) {
                    newPageSize -= 0
                    this.page = Math.floor((this.page || 0) * this.pageSize / newPageSize)
                    this.pageSize = newPageSize
                }
            }
        },

        watch: {
            page: {
                immediate: true,
                handler(newPage) {
                    this.$emit('update:currentPage', newPage)
                }
            },
            pageSize: {
                immediate: true,
                handler(newPageSize) {
                    this.$emit('update:currentPageSize', newPageSize)
                }
            },
            recordCount() {
                this.page = 0
            }
        }
    }
</script>
